import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { AuthProvider, landingRoute, useAuth } from '@/auth/AuthContext';
import { BuildingProvider, useBuilding } from '@/auth/BuildingContext';
import { RequireAuth } from '@/auth/RequireAuth';
import { RequirePasswordChange } from '@/auth/RequirePasswordChange';
import { RequireBuilding } from '@/auth/RequireBuilding';
import { RequireRole } from '@/auth/RequireRole';
import { PublicShell } from '@/components/PublicShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ConnectionProvider } from '@/lib/useConnection';
import { SiteProvider } from '@/lib/useSite';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Primitives';
import { isValidPublicId } from '@/lib/validation';
import { useDirection } from '@/lib/useDirection';
import { ToastProvider } from '@/lib/useToast';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AdminConsolePage = lazy(() => import('@/pages/AdminConsolePage'));
const AdminBuildingsPage = lazy(() => import('@/pages/AdminBuildingsPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ResidentHomePage = lazy(() => import('@/pages/ResidentHomePage'));
const UnitsPage = lazy(() => import('@/pages/UnitsPage'));
const UnitEditPage = lazy(() => import('@/pages/UnitEditPage'));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage'));
const ExpensesPage = lazy(() => import('@/pages/ExpensesPage'));
// #137: دفتر المصاريف للقراءة — للساكن والمسؤول معاً.
const ExpensesLedgerPage = lazy(() => import('@/pages/ExpensesLedgerPage'));
const TicketsPage = lazy(() => import('@/pages/TicketsPage'));
const TransparencyPage = lazy(() => import('@/pages/TransparencyPage'));
const AnnouncementsPage = lazy(() => import('@/pages/AnnouncementsPage'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage'));
const AdminBillingPage = lazy(() => import('@/pages/AdminBillingPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const AdminSitePage = lazy(() => import('@/pages/AdminSitePage'));
const AdminOnboardPage = lazy(() => import('@/pages/AdminOnboardPage'));
const AdminObservabilityPage = lazy(() => import('@/pages/AdminObservabilityPage'));
const FundPage = lazy(() => import('@/pages/FundPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const MembersPage = lazy(() => import('@/pages/MembersPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const PollsPage = lazy(() => import('@/pages/PollsPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ReceiptPage = lazy(() => import('@/pages/ReceiptPage'));
const AdminInboxPage = lazy(() => import('@/pages/AdminInboxPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));

function PageFallback() {
  return (
    <div style={{ display: 'grid', gap: 14, padding: 24 }}>
      <Skeleton height={120} />
      <Skeleton height={220} />
    </div>
  );
}

/** `/` sends each role to the right home. */
/**
 * Signed in? Straight to work. Otherwise the public front door — not the
 * login form. A visitor who has never heard of this needs to be told what it
 * is before being asked for a phone number.
 */
/*
  الجذر: لوحة لمن دخل، وصفحة تعريفية لمن لم يدخل.

  والتحويل صحيح كافتراض — المسؤول يفتح الموقع ليعمل لا ليقرأ عن المنتج،
  وإجباره على خطوة إضافية كل مرة إزعاج يومي. لكنه كان **بلا مخرج**: من
  أراد أن يرى صفحة التسعير أو يشاركها لا يستطيع ما دام مسجّلاً.

  فأُضيف `/home` مساراً صريحاً يعرضها دائماً، والتحويل يبقى على `/`.
  (وصفحة الساكن على `/me` — انظر #46 أدناه.)
  الافتراض للأغلبية، والمخرج لمن طلبه.
*/
function RootRedirect() {
  const { isAuthenticated, roles } = useAuth();
  if (isAuthenticated) return <Navigate to={landingRoute(roles)} replace />;
  return <LandingPage />;
}

/** The sidebar links to `/transparency`; resolve it to the active building. */
function TransparencyRedirect() {
  const { activeBuilding } = useBuilding();
  if (!activeBuilding) return <PageFallback />;
  return <Navigate to={`/t/${activeBuilding.publicId}`} replace />;
}

/** Rejects a malformed public id before the page ever mounts. */
function TransparencyRoute() {
  const { publicId } = useParams<{ publicId: string }>();
  if (!isValidPublicId(publicId)) return <NotFound />;
  return <TransparencyPage />;
}

function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="not-found">
      <Building2 size={35} />
      <h1>{t('errors.pageNotFound')}</h1>
      <button type="button" className="button" onClick={() => navigate('/')}>
        {t('errors.backHome')}
      </button>
    </div>
  );
}

/**
 * Scopes the boundary to the current route: a crash on /expenses is contained
 * there, and navigating anywhere else clears it instead of stranding the user
 * on an error screen that outlives its cause.
 */
function RouteErrorBoundary() {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Outlet />
    </ErrorBoundary>
  );
}

function Shell() {
  useDirection();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/t/:publicId" element={<TransparencyRoute />} />
        <Route path="/guide" element={<SupportPage />} />
        <Route path="/signup" element={<SignupPage />} />
        {/*
          المستندان القانونيان عامّان بلا استثناء: من يقرأ الشروط قبل أن
          يسجّل هو بالضبط من يجب أن يقرأها، ووضعهما خلف تسجيل الدخول يعني
          أن الموافقة تسبق الاطّلاع.
        */}
        <Route path="/terms" element={<PublicShell><TermsPage /></PublicShell>} />
        <Route path="/privacy" element={<PublicShell><PrivacyPage /></PublicShell>} />
        {/* عامّتان بلا تسجيل دخول: من يسأل قبل أن يشترك لا حساب له،
            ومن يفتح وصله من رسالة لا يُجبر على الدخول. */}
        <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />
        <Route path="/receipt/:token" element={<PublicShell><ReceiptPage /></PublicShell>} />

        {/* Authenticated */}
        <Route element={<RequireAuth />}>
          {/*
            كلمة سر ضبطها غير صاحبها تحجز المستخدم في `/account` حتى
            يبدّلها. الحارس **داخل** RequireAuth: من لم يسجّل دخوله لا شيء
            يُجبَر عليه. والعلم يأتي من `/auth/me` في كل تحميل، فتحديث
            الصفحة لا يتخطّاه.
          */}
          <Route element={<RequirePasswordChange />}>
          {/*
            صفحة الساكن على `/me` لا `/home` (#46).

            `/home` معرَّفة أعلاه للصفحة التعريفية العامة، وكانت معرَّفة هنا
            مرة ثانية للساكن. الراوتر يرتّب المسارات بالدقّة ثم بترتيب
            التعريف — والمساران متطابقان، فالأول يفوز دائماً: الساكن يسجّل
            دخوله ويرى صفحة التسويق. اختبار الواجهة لم يلتقطها لأنه كان
            يفحص المسار لا المحتوى (`tests/ui.mjs` يفحص الاثنين الآن).
          */}
          <Route path="/me" element={<ResidentHomePage />} />
          <Route path="/transparency" element={<TransparencyRedirect />} />

          <Route element={<Layout />}>
            <Route element={<RouteErrorBoundary />}>
            {/*
              فريق المنصّة: المالك والمحرّر. المحرّر ينشئ العمارات ويدير
              المستخدمين ويصحّح بياناتهم — وهذا كل عمل الدعم.
            */}
            <Route element={<RequireRole allow={['admin', 'editor']} />}>
              <Route path="/admin" element={<AdminConsolePage />} />
              <Route path="/admin/buildings" element={<AdminBuildingsPage />} />
              <Route path="/admin/onboard" element={<AdminOnboardPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
            </Route>

            {/*
              المالك وحده: التسعير وصفحة الهبوط وإيقاف العمارات. الفصل صريح
              هنا لا مشتقّ، فمراجعته أسهل من تتبّع شروط متفرّقة.
            */}
            <Route element={<RequireRole allow={['admin']} />}>
              <Route path="/admin/billing" element={<AdminBillingPage />} />
              <Route path="/admin/site" element={<AdminSitePage />} />
              <Route path="/admin/observability" element={<AdminObservabilityPage />} />
            </Route>

            {/*
              Order matters: the role check runs first so an unauthorised user
              is redirected immediately, never parked on a loading state.
            */}
            <Route element={<RequireRole allow={['admin', 'editor', 'manager']} />}>
              <Route element={<RequireBuilding />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/units" element={<UnitsPage />} />
                <Route path="/units/:id" element={<UnitEditPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
              </Route>
            </Route>

            {/* The manual: no role and no building needed to read it. */}
            <Route element={<RequireRole allow={['admin', 'manager']} />}>
              <Route path="/fund" element={<FundPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              {/* كان في القائمة بلا مسار — رابط ميت يرجع «الصفحة غير موجودة». */}
              <Route path="/users" element={<MembersPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/admin/inbox" element={<AdminInboxPage />} />
            </Route>
            {/* التصويت لكل من له دور في العمارة، لا للمسؤولين وحدهم —
                وهذا هو الفرق بين استطلاع رأي وبين إعلان. */}
            <Route path="/polls" element={<PollsPage />} />
            <Route element={<RequireRole allow={['admin', 'manager']} />}>
            </Route>
            {/* كانت في شبكة الخدمات بلا مسار — ثاني رابط ميّت بعد /users. */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/support" element={<SupportPage />} />
            {/* ونفس المستندين داخل الغلاف، حتى لا يخرج المستخدم المسجَّل
                من التطبيق ليقرأ سياسة الخصوصية ثم يعود بلا سياق. */}
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/account" element={<AccountPage />} />

            {/* Shared with residents, always scoped to their own building */}
            <Route element={<RequireBuilding />}>
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              {/* #137: دفتر المصاريف — الساكن يقرأ، والمسؤول يراه كما يراه الساكن. */}
              <Route path="/expenses-ledger" element={<ExpensesLedgerPage />} />
              {/* #51: الإعلانات لكل أدوار العمارة؛ الصفحة تُخفي الإرسال والحذف عن الساكن. */}
              <Route path="/announcements" element={<AnnouncementsPage />} />
              {/* #77: رسائل السكان — للمسؤول؛ الصفحة نفسها تطلب الإدارة. */}
              <Route element={<RequireRole allow={['admin', 'manager']} />}>
                <Route path="/messages" element={<MessagesPage />} />
              </Route>
            </Route>
            </Route>
          </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    // Outermost boundary: catches anything the providers themselves throw,
    // including a malformed session blob that slips past validation.
    <ErrorBoundary variant="app">
      {/* Outside the router so the banner survives navigation, and outside
          AuthProvider so a dead API does not look like a signed-out session. */}
      <ConnectionProvider>
        {/* Branding, contact details and prices — fetched once, read by the
            header, the footer, the landing page and the support page. */}
        <SiteProvider>
        <BrowserRouter>
          <AuthProvider>
            <BuildingProvider>
              <ToastProvider>
                <Shell />
              </ToastProvider>
            </BuildingProvider>
          </AuthProvider>
        </BrowserRouter>
        </SiteProvider>
      </ConnectionProvider>
    </ErrorBoundary>
  );
}
